import { Controller } from "@hotwired/stimulus"

/**
 * Markdown Editor Controller
 *
 * Provides a rich Markdown editing experience with:
 * - Toolbar with formatting buttons
 * - Live preview
 * - Keyboard shortcuts
 * - Undo/Redo support
 *
 * Usage:
 * <div data-controller="markdown-editor">
 *   <textarea data-markdown-editor-target="input"></textarea>
 *   <div data-markdown-editor-target="preview"></div>
 * </div>
 */
export default class extends Controller {
  static targets = ["input", "preview", "toolbar"]

  static values = {
    previewEnabled: { type: Boolean, default: true }
  }

  connect() {
    this.setupKeyboardShortcuts()
    this.updatePreview()

    // Update preview on input
    this.inputTarget.addEventListener('input', () => this.updatePreview())
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', () => this.updatePreview())
  }

  // ==================== FORMATTING ACTIONS ====================

  bold() {
    this.wrapSelection('**', '**', 'texte en gras')
  }

  italic() {
    this.wrapSelection('*', '*', 'texte en italique')
  }

  strikethrough() {
    this.wrapSelection('~~', '~~', 'texte barré')
  }

  code() {
    this.wrapSelection('`', '`', 'code')
  }

  codeBlock() {
    this.wrapSelection('```\n', '\n```', 'bloc de code')
  }

  heading(event) {
    const level = event.currentTarget.dataset.level || 2
    const prefix = '#'.repeat(level) + ' '
    this.insertAtLineStart(prefix, `Titre niveau ${level}`)
  }

  link() {
    this.wrapSelection('[', '](url)', 'texte du lien')
  }

  image() {
    this.wrapSelection('![', '](url)', 'description image')
  }

  quote() {
    this.insertAtLineStart('> ', 'Citation')
  }

  unorderedList() {
    this.insertAtLineStart('- ', 'Élément de liste')
  }

  orderedList() {
    this.insertAtLineStart('1. ', 'Premier élément')
  }

  horizontalRule() {
    this.insertText('\n\n---\n\n')
  }

  // ==================== HELPER METHODS ====================

  /**
   * Wraps the current selection with prefix and suffix
   * If no selection, inserts placeholder text
   */
  wrapSelection(prefix, suffix, placeholder = '') {
    const textarea = this.inputTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = textarea.value.substring(start, end)
    const textToInsert = selectedText || placeholder

    const before = textarea.value.substring(0, start)
    const after = textarea.value.substring(end)

    const newText = before + prefix + textToInsert + suffix + after

    textarea.value = newText

    // Set cursor position
    if (selectedText) {
      // If there was a selection, select the wrapped text
      textarea.setSelectionRange(start + prefix.length, end + prefix.length)
    } else {
      // If no selection, select the placeholder
      textarea.setSelectionRange(
        start + prefix.length,
        start + prefix.length + placeholder.length
      )
    }

    textarea.focus()
    this.updatePreview()
  }

  /**
   * Inserts text at the beginning of the current line
   */
  insertAtLineStart(prefix, placeholder = '') {
    const textarea = this.inputTarget
    const start = textarea.selectionStart
    const text = textarea.value

    // Find the start of the current line
    let lineStart = start
    while (lineStart > 0 && text[lineStart - 1] !== '\n') {
      lineStart--
    }

    // Check if prefix already exists
    const lineEnd = text.indexOf('\n', lineStart)
    const currentLine = lineEnd === -1
      ? text.substring(lineStart)
      : text.substring(lineStart, lineEnd)

    if (currentLine.startsWith(prefix)) {
      // Remove prefix if it already exists
      const before = text.substring(0, lineStart)
      const after = text.substring(lineStart + prefix.length)
      textarea.value = before + after
      textarea.setSelectionRange(start - prefix.length, start - prefix.length)
    } else {
      // Add prefix
      const before = text.substring(0, lineStart)
      const after = text.substring(lineStart)

      if (currentLine.trim() === '' && placeholder) {
        // Empty line: insert prefix + placeholder
        textarea.value = before + prefix + placeholder + after.substring(currentLine.length)
        textarea.setSelectionRange(
          lineStart + prefix.length,
          lineStart + prefix.length + placeholder.length
        )
      } else {
        // Non-empty line: just add prefix
        textarea.value = before + prefix + after
        textarea.setSelectionRange(start + prefix.length, start + prefix.length)
      }
    }

    textarea.focus()
    this.updatePreview()
  }

  /**
   * Inserts text at cursor position
   */
  insertText(text) {
    const textarea = this.inputTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    const before = textarea.value.substring(0, start)
    const after = textarea.value.substring(end)

    textarea.value = before + text + after
    textarea.setSelectionRange(start + text.length, start + text.length)
    textarea.focus()
    this.updatePreview()
  }

  // ==================== PREVIEW ====================

  togglePreview() {
    this.previewEnabledValue = !this.previewEnabledValue

    if (this.hasPreviewTarget) {
      if (this.previewEnabledValue) {
        this.previewTarget.classList.remove('hidden')
        this.updatePreview()
      } else {
        this.previewTarget.classList.add('hidden')
      }
    }
  }

  updatePreview() {
    if (!this.previewEnabledValue || !this.hasPreviewTarget) return

    const markdown = this.inputTarget.value
    const html = this.markdownToHtml(markdown)
    this.previewTarget.innerHTML = html
  }

  /**
   * Simple Markdown to HTML converter
   * For production, consider using a library like marked.js
   */
  markdownToHtml(markdown) {
    if (!markdown) return '<p class="text-gray-400 italic">La prévisualisation apparaîtra ici...</p>'

    let html = markdown

    // Escape HTML
    html = html.replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')

    // Code blocks (must be before inline code)
    html = html.replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-3 rounded my-2 overflow-x-auto"><code>$1</code></pre>')

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded text-sm">$1</code>')

    // Headers
    html = html.replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>')
    html = html.replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>')
    html = html.replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')

    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')

    // Strikethrough
    html = html.replace(/~~(.*?)~~/g, '<del class="line-through">$1</del>')

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:underline">$1</a>')

    // Images
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="max-w-full h-auto my-2 rounded" />')

    // Horizontal rule
    html = html.replace(/^---$/gm, '<hr class="my-4 border-gray-300" />')

    // Quotes
    html = html.replace(/^&gt; (.*$)/gm, '<blockquote class="border-l-4 border-gray-300 pl-4 italic my-2">$1</blockquote>')

    // Unordered lists
    html = html.replace(/^\- (.*$)/gm, '<li class="ml-4">$1</li>')
    html = html.replace(/(<li class="ml-4">.*<\/li>\n?)+/g, '<ul class="list-disc list-inside my-2">$&</ul>')

    // Ordered lists
    html = html.replace(/^\d+\. (.*$)/gm, '<li class="ml-4">$1</li>')
    html = html.replace(/(<li class="ml-4">.*<\/li>\n?)+/g, '<ol class="list-decimal list-inside my-2">$&</ol>')

    // Paragraphs (lines with content)
    html = html.split('\n\n').map(para => {
      para = para.trim()
      if (para && !para.match(/^<(h[1-3]|ul|ol|pre|blockquote|hr)/)) {
        return `<p class="my-2">${para}</p>`
      }
      return para
    }).join('\n')

    return html
  }

  // ==================== KEYBOARD SHORTCUTS ====================

  setupKeyboardShortcuts() {
    this.inputTarget.addEventListener('keydown', (event) => {
      // Ctrl/Cmd + B = Bold
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault()
        this.bold()
      }

      // Ctrl/Cmd + I = Italic
      if ((event.ctrlKey || event.metaKey) && event.key === 'i') {
        event.preventDefault()
        this.italic()
      }

      // Ctrl/Cmd + K = Link
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault()
        this.link()
      }

      // Ctrl/Cmd + E = Code
      if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
        event.preventDefault()
        this.code()
      }

      // Ctrl/Cmd + Shift + C = Code Block
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'C') {
        event.preventDefault()
        this.codeBlock()
      }

      // Tab = Insert 2 spaces (or 4 if you prefer)
      if (event.key === 'Tab') {
        event.preventDefault()
        this.insertText('  ')
      }
    })
  }

  // ==================== UTILITY ====================

  clear() {
    this.inputTarget.value = ''
    this.updatePreview()
    this.inputTarget.focus()
  }

  undo() {
    document.execCommand('undo')
    this.updatePreview()
  }

  redo() {
    document.execCommand('redo')
    this.updatePreview()
  }

  fullscreen() {
    const container = this.element

    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err)
      })
    } else {
      document.exitFullscreen()
    }
  }
}
